import { useState, useEffect } from "preact/hooks"
import { listConversations, WizardConversation } from "@/api/wizard"
import { X, Plus, MessageSquare } from "lucide-react"
import { format, parseISO } from "date-fns"

interface ConversationHistoryProps {
  onSelect: (id: string) => void
  onNew: () => void
  onClose: () => void
  activeId: string | null
  className?: string
}

export function ConversationHistory({ onSelect, onNew, onClose, activeId, className }: ConversationHistoryProps) {
  const [conversations, setConversations] = useState<WizardConversation[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadConversations()
  }, [])

  async function loadConversations() {
    try {
      const convs = await listConversations()
      setConversations(convs)
    } catch {
      // Silently fail
    } finally {
      setLoading(false)
    }
  }

  return (
    <div class={`w-72 bg-gray-50 border-r border-gray-200 flex flex-col h-full ${className || ""}`}>
      <div class="flex items-center justify-between px-4 py-3 border-b border-gray-200">
        <h3 class="text-sm font-semibold text-gray-700">History</h3>
        <div class="flex items-center gap-1">
          <button
            onClick={onNew}
            class="p-1.5 rounded-lg hover:bg-gray-200 text-gray-500"
            title="New conversation"
          >
            <Plus className="w-4 h-4" />
          </button>
          <button
            onClick={onClose}
            class="p-1.5 rounded-lg hover:bg-gray-200 text-gray-500"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div class="flex-1 overflow-y-auto">
        {loading ? (
          <div class="p-4 text-sm text-gray-400 text-center">Loading...</div>
        ) : conversations.length === 0 ? (
          <div class="p-4 text-sm text-gray-400 text-center">No conversations yet</div>
        ) : (
          <div class="py-1">
            {conversations.map((conv: WizardConversation) => (
              <ConversationItem
                key={conv.id}
                conversation={conv}
                isActive={conv.id === activeId}
                onSelect={() => onSelect(conv.id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

interface ConversationItemProps {
  conversation: WizardConversation
  isActive: boolean
  onSelect: () => void
}

function ConversationItem({ conversation, isActive, onSelect }: ConversationItemProps) {
  const title = conversation.title || "New conversation"
  const date = format(parseISO(conversation.updated_at), "MMM d, h:mm a")

  return (
    <button
      type="button"
      onClick={onSelect}
      class={`w-full px-4 py-3 text-left hover:bg-gray-100 transition-colors ${
        isActive ? "bg-primary/5 border-l-2 border-primary" : ""
      }`}
    >
      <div class="flex items-start gap-2">
        <MessageSquare className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
        <div class="flex-1 min-w-0">
          <p class="text-sm font-medium text-gray-700 truncate">{title}</p>
          <p class="text-xs text-gray-400 mt-0.5">{date}</p>
        </div>
      </div>
    </button>
  )
}
